import { TextAssist } from '../comments/TextAssist';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AREA_KIND_ORDER, type AreaKind } from '../../domain/areaCatalog';
import type { V2Evidence } from '../../domain/evidence';
import { FINDING_PRIORITY_ORDER, findingCanSave, findingExportIncomplete, type FindingOrigin, type FindingPriority, type V2Finding } from '../../domain/finding';
import { RESPONSIBLE_TRADE_ORDER, canonicalResponsibleTrade, type ResponsibleTrade } from '../../domain/responsibleTrade';
import { useT } from '../../i18n/i18n';
import { areaKindLabel, findingClassLabel, priorityLabel, tradeLabel } from '../../i18n/labels';
import type { EvidenceTarget } from '../../state/captureFlow';
import { useStore } from '../../state/createStore';
import { evidenceForIds, evidenceStore } from '../../state/evidenceStore';
import {
  addFindingAction,
  commitFindingAction,
  deleteFindingAction,
  findingStore,
  setFindingStatusAction,
  updateFindingAction
} from '../../state/findingStore';
import { Button, ConfirmSheet, Disclosure, Dropdown, Field, InlineMessage, SectionEyebrow, SegmentedControl, TextArea, TextInput, ToggleChip, showToast } from '../../ui/components';
import { PhotoStrip } from '../evidence/PhotoStrip';
import { containsChinese } from '../../report/boundText';
import { recommendationAfterTradeChange } from '../../state/aiFindingDraft';

// Finding editor (Authority §10; task book §14 — progressive, field-first, never the old big form):
//   1 Evidence (拍照 · 相册 · 照片池)  2 Observation  3 Recommendation  4 Save
// with a short Finding Title on top (D-16 minimum = title + observation), and the report-only fields
// (Report Section · Impact / Priority · Responsible Trade) folded into 报告设置. ONE FINDING SAVE MODEL
// (D-15): a NEW finding is autosaved as a local DRAFT while edited (not in the Report Draft); the
// explicit Save commits it; Cancel discards the uncommitted draft. AI drafting is optional and this
// build has no AI lane connected, so no AI button is shown. The host owns the sticky actions.
export interface FindingEditorHandle {
  save: () => Promise<boolean>;
  cancel: () => void;
}

export interface FindingEditorState {
  canSave: boolean;
  dirty: boolean;
  draft: boolean;
}

// Starting content for a NEW finding (Round 2 §Q "Modify": the AI suggestion's text and photos are
// pre-filled; the inspector edits and saves — the saved record is the inspector's).
export interface FindingPrefill {
  title?: string;
  observation?: string;
  recommendation?: string;
  responsibleTrade?: ResponsibleTrade | null;
  evidenceIds?: string[];
}

type Props = {
  inspectionId: string;
  areaId: string;
  areaKind: AreaKind;
  existing: V2Finding | null;
  prefill?: FindingPrefill;
  origin?: FindingOrigin;
  locked?: boolean;
  onDone: (saved: boolean) => void;
  onSaved?: (findingId: string) => void;
  onOpenLightbox: (evidence: V2Evidence[], index: number, findingId: string | null) => void;
  onState?: (state: FindingEditorState) => void;
};

export const FindingEditor = forwardRef<FindingEditorHandle, Props>(function FindingEditor(props, ref) {
  const { t } = useT();
  const existing = props.existing;
  const locked = props.locked === true;
  const liveExisting = useStore(findingStore, (s) => (existing ? (s.findings.find((f) => f.id === existing.id) ?? existing) : null));
  const evidenceState = useStore(evidenceStore, (s) => s);
  const prefill = props.prefill;
  const [title, setTitle] = useState(existing?.title ?? prefill?.title ?? '');
  const [observation, setObservation] = useState(existing?.observation ?? prefill?.observation ?? '');
  const [recommendation, setRecommendation] = useState(existing?.recommendation ?? prefill?.recommendation ?? '');
  const [trade, setTrade] = useState<ResponsibleTrade | null>(canonicalResponsibleTrade(existing?.responsibleTrade ?? prefill?.responsibleTrade ?? null));
  // Ratification 2026-09-13 §4: REFERRAL = ROW + INSPECTOR-CONFIRMED FACT + AUTHORITY — the PE is reached only here.
  const [structuralConcern, setStructuralConcern] = useState<boolean>(existing?.structuralConcern ?? false);
  const [tradeOther, setTradeOther] = useState(existing?.responsibleTradeOther ?? '');
  const [section, setSection] = useState<string>(existing?.reportSection && existing.reportSection.length > 0 ? existing.reportSection : props.areaKind);
  const [priority, setPriority] = useState<FindingPriority>(existing?.priority ?? 3);
  const [pendingEvidenceIds, setPendingEvidenceIds] = useState<string[]>(() => (existing ? [] : [...new Set(prefill?.evidenceIds ?? [])]));
  const [draftId, setDraftId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showMinimum, setShowMinimum] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const draftIdRef = useRef<string | null>(null);
  const pendingRef = useRef<string[]>([]);
  pendingRef.current = pendingEvidenceIds;

  const record = liveExisting ?? (draftId ? (findingStore.get().findings.find((f) => f.id === draftId) ?? null) : null);
  const liveDraft = useStore(findingStore, (s) => (draftId ? (s.findings.find((f) => f.id === draftId) ?? null) : null));
  const linkedIds = liveExisting ? liveExisting.evidenceIds : liveDraft ? [...new Set([...liveDraft.evidenceIds, ...pendingEvidenceIds])] : pendingEvidenceIds;
  const linked = useMemo(() => evidenceForIds(evidenceState, linkedIds), [evidenceState, linkedIds]);
  const findingId = liveExisting ? liveExisting.id : draftId;

  const edit = useMemo(
    () => ({ title, observation, recommendation, responsibleTrade: trade, responsibleTradeOther: tradeOther, reportSection: section, priority, structuralConcern }),
    [title, observation, recommendation, trade, tradeOther, section, priority, structuralConcern]
  );
  const dirty = existing
    ? title !== existing.title ||
      observation !== existing.observation ||
      recommendation !== existing.recommendation ||
      trade !== existing.responsibleTrade ||
      tradeOther !== existing.responsibleTradeOther ||
      section !== (existing.reportSection || props.areaKind) ||
      priority !== existing.priority ||
      structuralConcern !== existing.structuralConcern
    : title.trim().length > 0 || observation.trim().length > 0 || recommendation.trim().length > 0 || pendingEvidenceIds.length > 0;
  const canSave = findingCanSave({ title, observation }) && !busy && !locked;
  const isDraft = !existing || existing.status === 'draft';
  const exportIncomplete = findingExportIncomplete({ findingClass: existing?.findingClass ?? 'defect', ...edit });

  useEffect(() => {
    props.onState?.({ canSave, dirty, draft: isDraft });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the host only needs the derived flags
  }, [canSave, dirty, isDraft]);

  // Local draft autosave (new findings only): every change lands on this device after a short idle.
  const hasContent = title.trim().length > 0 || observation.trim().length > 0;
  useEffect(() => {
    if (existing || !hasContent || locked) return undefined;
    const timer = setTimeout(() => {
      void (async () => {
        if (draftIdRef.current) {
          await updateFindingAction(draftIdRef.current, edit);
        } else {
          const created = await addFindingAction({ inspectionId: props.inspectionId, areaId: props.areaId, ...edit, status: 'draft', evidenceIds: pendingRef.current, origin: props.origin });
          if (created) {
            draftIdRef.current = created.id;
            setDraftId(created.id);
          }
        }
      })();
    }, 600);
    return () => clearTimeout(timer);
  }, [edit, existing, hasContent, locked, props.inspectionId, props.areaId]);

  async function save(): Promise<boolean> {
    if (!findingCanSave({ title, observation })) {
      setShowMinimum(true);
      return false;
    }
    setBusy(true);
    try {
      let savedId: string | null = null;
      if (existing) {
        if (existing.status === 'draft') await commitFindingAction(existing.id, edit);
        else await updateFindingAction(existing.id, edit);
        savedId = existing.id;
      } else if (draftIdRef.current) {
        await commitFindingAction(draftIdRef.current, edit);
        savedId = draftIdRef.current;
      } else {
        const created = await addFindingAction({ inspectionId: props.inspectionId, areaId: props.areaId, ...edit, evidenceIds: pendingRef.current, origin: props.origin });
        savedId = created?.id ?? null;
      }
      showToast(t('finding.savedToReportDraft'));
      if (savedId) props.onSaved?.(savedId);
      props.onDone(true);
      return true;
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    if ((dirty || draftIdRef.current) && !locked) setConfirmDiscard(true);
    else props.onDone(false);
  }

  async function discard() {
    setConfirmDiscard(false);
    if (!existing && draftIdRef.current) await deleteFindingAction(draftIdRef.current);
    props.onDone(false);
  }

  useImperativeHandle(ref, () => ({ save, cancel }));

  async function toggleWithdrawn() {
    if (!existing) return;
    await setFindingStatusAction(existing.id, existing.status === 'rejected' ? 'confirmed' : 'rejected');
  }

  async function deleteRecord() {
    if (!existing) return;
    setConfirmDelete(false);
    await deleteFindingAction(existing.id);
    props.onDone(false);
  }

  // 2026-10-28 (device walkthrough A5): the recommendation that is still exactly the referral of the old trade
  // follows a new trade, so the sentence and the setting never name two professionals.
  function changeTrade(next: ResponsibleTrade | null) {
    setRecommendation((current) => recommendationAfterTradeChange(current, trade, next));
    setTrade(next);
  }

  function handleCaptured(evidence: V2Evidence[]) {
    // With a finding record in place captureFlow linked the photo in its own transaction;
    // before that it is remembered and linked when the draft / finding is written.
    if (!findingId) setPendingEvidenceIds((prev) => [...prev, ...evidence.map((e) => e.id)]);
  }

  function handleLinked(ids: string[]) {
    if (!findingId) setPendingEvidenceIds((prev) => [...new Set([...prev, ...ids])]);
  }

  // 2026-10-28 (iPhone real-photo run 10-26 #8): these three fields print as written in the English client
  // report, and Chinese there blocks the report at the closeout — said right under the field, while writing.
  const chineseNote = (text: string, testId: string) => (containsChinese(text) ? <p className="msg msg-err" data-testid={testId}>{t('finding.chineseInClientReport')}</p> : null);

  const target: EvidenceTarget = { inspectionId: props.inspectionId, areaId: props.areaId, itemId: null, findingId };
  const statusRecord = record ?? liveDraft;
  const sectionLabel = (AREA_KIND_ORDER as readonly string[]).includes(section) ? areaKindLabel(t, section as AreaKind) : section;
  const settingsSummary = [sectionLabel, priorityLabel(t, priority), trade ? (trade === 'other' && tradeOther ? tradeOther : tradeLabel(t, trade)) : t('finding.tradeMissing')].join(' · ');

  return (
    <div className="finding-editor" data-testid="finding-editor" data-finding-id={findingId ?? ''}>
      <div className="finding-editor-head">
        <SectionEyebrow>{existing ? t('finding.editTitle') : t('finding.otherTitle')}</SectionEyebrow>
        <span className="badge badge-muted" data-testid="finding-class-label">
          {findingClassLabel(t, existing?.findingClass ?? 'defect')}
        </span>
      </div>

      <Field label={t('finding.title')} required htmlFor="finding-title">
        <TextInput id="finding-title" value={title} onChange={setTitle} placeholder={t('finding.titlePlaceholder')} disabled={locked} testId="finding-title" />
        {chineseNote(title, 'finding-title-chinese')}
      </Field>

      <div className="finding-photos" data-testid="finding-evidence-step">
        <SectionEyebrow tone="primary">{`${t('finding.stepEvidence')} (${linked.length})`}</SectionEyebrow>
        <PhotoStrip
          evidence={linked}
          target={target}
          layout="grid"
          actions="pair"
          pool={{ itemId: null, findingId, linkedIds, onLinked: handleLinked }}
          disabled={locked}
          onOpen={(index) => props.onOpenLightbox(linked, index, findingId)}
          onCaptured={handleCaptured}
          emptyText={t('item.evidenceEmpty')}
          testId="finding-photos"
        />
      </div>

      <Field label={t('finding.stepObservation')} required htmlFor="finding-observation">
        <TextArea id="finding-observation" value={observation} onChange={setObservation} placeholder={t('finding.observationPlaceholder')} disabled={locked} testId="finding-observation" />
        {chineseNote(observation, 'finding-observation-chinese')}
        <TextAssist value={observation} onChange={setObservation} fieldId="finding-observation" lang="en-US" disabled={locked} testId="finding-observation-assist" />
      </Field>

      <Field label={t('finding.stepRecommendation')} htmlFor="finding-recommendation" hint={t('finding.recommendationHint')}>
        <TextArea id="finding-recommendation" value={recommendation} onChange={setRecommendation} placeholder={t('finding.recommendationPlaceholder')} disabled={locked} testId="finding-recommendation" />
        {chineseNote(recommendation, 'finding-recommendation-chinese')}
        <TextAssist value={recommendation} onChange={setRecommendation} fieldId="finding-recommendation" lang="en-US" disabled={locked} testId="finding-recommendation-assist" />
      </Field>

      <Disclosure title={t('finding.reportSettings')} summary={settingsSummary} testId="finding-report-settings">
        <div className="form-stack finding-settings">
          <Field label={t('finding.reportSection')} htmlFor="finding-section">
            <Dropdown
              id="finding-section"
              value={(AREA_KIND_ORDER as readonly string[]).includes(section) ? (section as AreaKind) : null}
              onChange={(kind) => setSection(kind)}
              title={t('finding.reportSection')}
              disabled={locked}
              options={AREA_KIND_ORDER.map((kind) => ({ key: kind, label: areaKindLabel(t, kind) }))}
              testId="finding-section"
            />
          </Field>
          <div className="field">
            <div className="field-head">
              <span className="field-label">{t('priority.label')}</span>
              <span className="field-required" data-testid="finding-priority">
                {priorityLabel(t, priority)}
              </span>
            </div>
            <SegmentedControl
              variant="compact"
              ariaLabel={t('priority.label')}
              value={String(priority)}
              onChange={(key) => setPriority(Number(key) as FindingPriority)}
              options={FINDING_PRIORITY_ORDER.map((p) => ({ key: String(p), label: String(p), tone: 'error' as const, disabled: locked, testId: `finding-priority-${p}` }))}
              testId="finding-priority-segments"
            />
          </div>
          <div className="field" data-testid="finding-structural" data-on={structuralConcern ? '1' : '0'}>
            <ToggleChip
              label={t('finding.structuralConcern')}
              on={structuralConcern}
              onToggle={(on) => {
                if (locked) return;
                setStructuralConcern(on);
                changeTrade(on ? 'structuralEngineer' : trade === 'structuralEngineer' ? canonicalResponsibleTrade(prefill?.responsibleTrade ?? null) : trade);
              }}
              testId="finding-structural-toggle"
            />
            <p className="card-note">{t('finding.structuralConcernHint')}</p>
          </div>
          <Field label={t('finding.responsibleTrade')} htmlFor="finding-trade" hint={t('finding.tradeHint')}>
            <Dropdown
              id="finding-trade"
              value={trade}
              onChange={changeTrade}
              title={t('finding.responsibleTrade')}
              disabled={locked}
              options={RESPONSIBLE_TRADE_ORDER.map((code) => ({ key: code, label: tradeLabel(t, code) }))}
              testId="finding-trade"
            />
          </Field>
          {trade === 'other' ? (
            <Field label={t('finding.tradeOther')} required htmlFor="finding-trade-other">
              <TextInput id="finding-trade-other" value={tradeOther} onChange={setTradeOther} placeholder={t('finding.tradeOtherPlaceholder')} disabled={locked} testId="finding-trade-other" />
            </Field>
          ) : null}
        </div>
      </Disclosure>

      {showMinimum && !findingCanSave({ title, observation }) ? <InlineMessage kind="err" text={t('finding.minimum')} /> : null}
      <p className="finding-status-line" data-testid="finding-status-line">
        {statusRecord && statusRecord.status !== 'draft' ? t('finding.savedStatus') : t('finding.draftStatus')}
        {exportIncomplete ? ` · ${t('item.incomplete')}` : ''}
      </p>
      {exportIncomplete ? <p className="card-note">{t('finding.exportIncomplete')}</p> : null}

      {existing && existing.status !== 'draft' && !locked ? (
        <div className="btn-row finding-secondary-actions">
          <Button variant="secondary" size="small" onClick={() => void toggleWithdrawn()} disabled={busy} testId="finding-toggle-status">
            {existing.status === 'rejected' ? t('finding.restore') : t('finding.withdraw')}
          </Button>
          <Button variant="danger" size="small" onClick={() => setConfirmDelete(true)} disabled={busy} testId="finding-delete">
            {t('finding.deleteRecord')}
          </Button>
        </div>
      ) : null}

      {confirmDiscard ? (
        <ConfirmSheet title={t('finding.discardTitle')} body={t('finding.discardBody')} confirmLabel={t('finding.discard')} cancelLabel={t('finding.keepEditing')} danger onConfirm={() => void discard()} onCancel={() => setConfirmDiscard(false)} />
      ) : null}
      {confirmDelete ? (
        <ConfirmSheet title={t('finding.deleteTitle')} body={t('finding.deleteBody')} confirmLabel={t('common.delete')} danger onConfirm={() => void deleteRecord()} onCancel={() => setConfirmDelete(false)} />
      ) : null}
    </div>
  );
});
